/**
 * Vector layers for the Canvas 2D (OpenLayers) engine.
 *
 * The Canvas engine is what GPU-less / software-WebGL hosts fall back to, and
 * it used to render rasters only — a PMTiles or GeoJSON feature layer toggled
 * on in the scene tree simply never appeared. These factories give it the two
 * vector paths MapLibre already has:
 *
 *   - PMTiles (MVT) → `ol/layer/VectorTile` fed straight from the archive via
 *     a range-reading `PMTiles` client (no `pmtiles://` protocol hook needed).
 *   - GeoJSON / OGC Features JSON → `ol/layer/Vector`.
 *
 * Styling mirrors the MapLibre defaults so the same archive looks the same on
 * either engine: contour derivatives draw as thin dark hairlines with no fill,
 * everything else as accent-coloured outlines with a soft fill.
 */

import GeoJSON from 'ol/format/GeoJSON.js';
import MVT from 'ol/format/MVT.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorTileLayer from 'ol/layer/VectorTile.js';
import VectorSource from 'ol/source/Vector.js';
import VectorTileSource from 'ol/source/VectorTile.js';
import CircleStyle from 'ol/style/Circle.js';
import Fill from 'ol/style/Fill.js';
import Icon from 'ol/style/Icon.js';
import RegularShape from 'ol/style/RegularShape.js';
import Stroke from 'ol/style/Stroke.js';
import Style from 'ol/style/Style.js';
import Point from 'ol/geom/Point.js';
import { transformExtent } from 'ol/proj.js';
import type { Extent } from 'ol/extent.js';
import type { FeatureLike } from 'ol/Feature.js';
import type OlVectorTile from 'ol/VectorTile.js';
import type Projection from 'ol/proj/Projection.js';
import type RenderFeature from 'ol/render/Feature.js';
import { PMTiles } from 'pmtiles';

import type { Layer } from '@geosoft/viewer-sdk';
import { resolveVectorFallbackColor } from '@/design/tokenResolver';
import {
  isHighlightActive,
  propertiesMatchHighlight,
  VECTOR_IDENTIFY_HIGHLIGHT,
  type VectorHighlightTarget,
} from '@/components/vector/vectorIdentifyHighlight';
import {
  pointSymbolGeometryFor,
  pointSymbolRotationDeg,
  regularShapeParamsFor,
  type PointSymbolShape,
} from '@/components/vector/pointSymbolShape';
import { peekPointIcon, pointIconDataUrl } from '@/components/vector/pointIconRegistry';
import {
  createPolygonPatternCanvas,
  type PolygonSymbolState,
} from '@/components/vector/polygonSymbolRegistry';
import {
  uniqueValueKey,
  type UniqueValueSymbology,
} from '@/components/vector/vectorStyleModel';
import {
  lineDashArray,
  type LineSymbolState,
} from '@/components/vector/lineSymbolRegistry';
import { isContourPmtilesLayer, readPmtilesArchiveInfo } from '@/engines/pmtiles/sourceLayer';
import { transformGeoJsonCoordinates } from '@/utils/vectorCoordinateOverride';

/** Fallback zoom range when the PMTiles header cannot be read. */
const DEFAULT_MIN_ZOOM = 0;
const DEFAULT_MAX_ZOOM = 14;

export interface OlVectorStyleSpec {
  readonly color: string;
  readonly lineWidth: number;
  /** 0 for contours — a filled contour band hides the raster underneath. */
  readonly fillOpacity: number;
  readonly pointRadius: number;
  /** Defaults to `circle` when the ToolCard has not picked a symbol yet. */
  readonly pointShape?: PointSymbolShape;
  /** Clockwise degrees, already including the symbol's base rotation. */
  readonly pointRotationDeg?: number;
  /**
   * Raw symbol id. A namespaced id (e.g. `maki:bar`) draws the registry icon
   * once its set is in memory; `pointShape` is the fallback until then.
   */
  readonly pointSymbolId?: string;
  readonly polygonSymbol?: PolygonSymbolState;
  readonly lineSymbol?: LineSymbolState;
}

/** Minimal archive surface used by {@link pmtilesVectorTileLoader}. */
export interface PmtilesTileReader {
  getZxy(z: number, x: number, y: number): Promise<{ data: ArrayBuffer } | undefined>;
}

export function vectorStyleSpecForLayer(layer: Layer): OlVectorStyleSpec {
  const contours = isContourPmtilesLayer(layer);
  const declared = layer.style?.['color'];
  const color =
    typeof declared === 'string' && declared.length > 0
      ? declared
      : contours
        ? '#111827'
        : resolveVectorFallbackColor();
  return {
    color,
    lineWidth: contours ? 1 : 2,
    fillOpacity: contours ? 0 : 0.24,
    pointRadius: 4,
  };
}

function withAlpha(color: string, alpha: number): string {
  if (alpha >= 1) return color;
  const hex = /^#([0-9a-f]{6})$/i.exec(color.trim());
  if (hex) {
    const value = Number.parseInt(hex[1], 16);
    const r = (value >> 16) & 0xff;
    const g = (value >> 8) & 0xff;
    const b = value & 0xff;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}

/**
 * MVT tiles arrive as `RenderFeature`s (no geometry object), plain GeoJSON as
 * `ol/Feature`. Read the geometry type from whichever shape we were handed.
 */
export function geometryTypeOf(feature: FeatureLike): string | null {
  const candidate = feature as {
    getType?: () => string;
    getGeometry?: () => { getType?: () => string } | null | undefined;
  };
  if (typeof candidate.getType === 'function') return candidate.getType();
  const geometry = candidate.getGeometry?.();
  if (geometry && typeof geometry.getType === 'function') return geometry.getType();
  return null;
}

export function vectorStyleFunctionForLayer(
  layer: Layer,
): (feature: FeatureLike) => Style | Style[] {
  return vectorStyleFunctionFromSpec(vectorStyleSpecForLayer(layer));
}

/**
 * Live ToolCard paint — same geometry split as the mount-time style, but
 * stroke / fill can be different colours (MapLibre fill+line layers).
 */
export function vectorStyleFunctionFromPaint(
  delta: {
    readonly fillColor?: string;
    readonly fillOpacity?: number;
    readonly lineColor?: string;
    readonly lineWidth?: number;
    readonly lineSymbol?: LineSymbolState;
    readonly pointSize?: number;
    readonly pointSymbolId?: string;
    readonly pointRotation?: number;
    readonly polygonSymbol?: PolygonSymbolState;
    readonly uniqueValueSymbology?: UniqueValueSymbology | null;
  },
  options: { readonly selectedFeatureId?: VectorHighlightTarget } = {},
): (feature: FeatureLike) => Style | Style[] {
  if (delta.uniqueValueSymbology) {
    const unique = delta.uniqueValueSymbology;
    const classStyles = new Map(
      unique.classes.map((entry) => [
        uniqueValueKey(entry.value),
        vectorStyleFunctionFromPaint(
          {
            ...delta,
            fillColor: entry.fillColor,
            fillOpacity: entry.visible ? entry.fillOpacity : 0,
            lineColor: entry.strokeColor,
            lineWidth: entry.strokeWidth,
            lineSymbol: {
              ...entry.lineSymbol,
              color: entry.strokeColor,
              width: entry.strokeWidth,
            },
            polygonSymbol: entry.visible
              ? { ...entry.polygonSymbol, backgroundColor: entry.fillColor }
              : { ...entry.polygonSymbol, fillMode: 'none' },
            uniqueValueSymbology: null,
          },
          options,
        ),
      ]),
    );
    const hidden = new Style();
    return (feature: FeatureLike) => {
      const properties =
        typeof feature.getProperties === 'function'
          ? (feature.getProperties() as Record<string, unknown>)
          : {};
      const style = classStyles.get(uniqueValueKey(normalizeClassValue(properties[unique.field])));
      return style ? style(feature) : hidden;
    };
  }
  const fallback = resolveVectorFallbackColor();
  const lineColor = delta.lineColor ?? fallback;
  const fillColor = delta.fillColor ?? lineColor;
  const symbolId = delta.pointSymbolId ?? 'circle';
  const base = vectorStyleFunctionFromSpec({
    color: lineColor,
    lineWidth: delta.lineWidth ?? 2,
    fillOpacity: delta.fillOpacity ?? 0.24,
    // The ToolCard expresses point size as a diameter; OpenLayers wants a radius.
    pointRadius: (delta.pointSize ?? 16) / 2,
    pointShape: pointSymbolGeometryFor(symbolId).shape,
    pointRotationDeg: pointSymbolRotationDeg(symbolId, delta.pointRotation ?? 0),
    pointSymbolId: symbolId,
    polygonSymbol: delta.polygonSymbol,
    fillColor,
    lineColor,
    lineSymbol: delta.lineSymbol,
  });
  const selectedId = options.selectedFeatureId ?? null;
  if (!isHighlightActive(selectedId)) return base;
  const highlight = vectorStyleFunctionFromSpec({
    color: VECTOR_IDENTIFY_HIGHLIGHT.lineColor,
    lineWidth: VECTOR_IDENTIFY_HIGHLIGHT.lineWidth,
    fillOpacity: VECTOR_IDENTIFY_HIGHLIGHT.fillOpacity,
    // Keep the picked symbol so identify only recolours / enlarges the point.
    pointRadius: Math.max(VECTOR_IDENTIFY_HIGHLIGHT.pointRadius, (delta.pointSize ?? 16) / 2),
    pointShape: pointSymbolGeometryFor(symbolId).shape,
    pointRotationDeg: pointSymbolRotationDeg(symbolId, delta.pointRotation ?? 0),
    pointSymbolId: symbolId,
    fillColor: VECTOR_IDENTIFY_HIGHLIGHT.fillColor,
    lineColor: VECTOR_IDENTIFY_HIGHLIGHT.lineColor,
  });
  return (feature: FeatureLike) =>
    featureMatchesHighlight(feature, selectedId) ? highlight(feature) : base(feature);
}

function normalizeClassValue(value: unknown): string | number | boolean | null {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return String(value);
}

export function featureMatchesHighlight(
  feature: FeatureLike,
  selectedId: VectorHighlightTarget,
): boolean {
  const rawId = typeof feature.getId === 'function' ? feature.getId() : undefined;
  const properties =
    typeof feature.getProperties === 'function'
      ? (feature.getProperties() as Record<string, unknown>)
      : {};
  return propertiesMatchHighlight(
    properties,
    rawId as string | number | null | undefined,
    selectedId,
  );
}

function vectorStyleFunctionFromSpec(
  spec: OlVectorStyleSpec & {
    readonly fillColor?: string;
    readonly lineColor?: string;
    readonly lineSymbol?: LineSymbolState;
  },
): (feature: FeatureLike) => Style | Style[] {
  const lineSymbol = spec.lineSymbol;
  const lineColor = lineSymbol?.color ?? spec.lineColor ?? spec.color;
  const fillColor = spec.fillColor ?? spec.color;
  const dash = lineSymbol ? lineDashArray(lineSymbol.pattern) : null;
  const stroke = new Stroke({
    color: withAlpha(lineColor, lineSymbol?.opacity ?? 1),
    width: lineSymbol?.width ?? spec.lineWidth,
    lineDash: dash ? [...dash] : undefined,
    lineCap: lineSymbol?.cap,
    lineJoin: lineSymbol?.join,
  });
  const patternCanvas =
    spec.polygonSymbol
    && spec.polygonSymbol.fillMode !== 'solid'
    && spec.polygonSymbol.fillMode !== 'none'
      ? createPolygonPatternCanvas(spec.polygonSymbol, spec.fillOpacity)
      : null;
  const pattern = patternCanvas?.getContext('2d')?.createPattern(patternCanvas, 'repeat') ?? null;
  const polygon = new Style({
    stroke,
    fill:
      spec.fillOpacity > 0 && spec.polygonSymbol?.fillMode !== 'none'
        ? new Fill({ color: pattern ?? withAlpha(fillColor, spec.fillOpacity) })
        : undefined,
  });
  const line = new Style({ stroke });
  const casing = lineSymbol?.pattern === 'cased'
    ? new Style({
        stroke: new Stroke({
          color: withAlpha(lineSymbol.casingColor, lineSymbol.opacity),
          width: lineSymbol.width + lineSymbol.casingWidth * 2,
          lineCap: lineSymbol.cap,
          lineJoin: lineSymbol.join,
        }),
      })
    : null;
  const point = new Style({ image: pointImageFromSpec(spec, fillColor) });

  return (feature: FeatureLike) => {
    const type = geometryTypeOf(feature);
    if (type === 'Point' || type === 'MultiPoint') return point;
    if (type === 'Polygon' || type === 'MultiPolygon') return polygon;
    if (lineSymbol?.pattern === 'arrow') {
      const arrow = arrowStyleForFeature(feature, lineSymbol);
      return arrow ? [line, arrow] : line;
    }
    return casing ? [casing, line] : line;
  };
}

function arrowStyleForFeature(
  feature: FeatureLike,
  symbol: LineSymbolState,
): Style | null {
  const candidate = feature as {
    getGeometry?: () => {
      getCoordinates?: () => unknown;
      getLastCoordinate?: () => number[];
    } | null;
    getFlatCoordinates?: () => number[];
  };
  const geometry = candidate.getGeometry?.();
  let coordinates = geometry?.getLastCoordinate?.();
  let previous: number[] | undefined;
  const raw = geometry?.getCoordinates?.();
  if (Array.isArray(raw) && Array.isArray(raw[0])) {
    const points = raw as number[][];
    coordinates = points.at(-1);
    previous = points.at(-2);
  }
  const flat = candidate.getFlatCoordinates?.();
  if ((!coordinates || !previous) && flat && flat.length >= 4) {
    coordinates = flat.slice(-2);
    previous = flat.slice(-4, -2);
  }
  if (!coordinates || coordinates.length < 2) return null;
  const rotation = previous
    ? Math.atan2(coordinates[1]! - previous[1]!, coordinates[0]! - previous[0]!)
    : 0;
  return new Style({
    geometry: new Point([coordinates[0]!, coordinates[1]!]),
    image: new RegularShape({
      points: 3,
      radius: Math.max(5, symbol.width * 2.5),
      fill: new Fill({ color: withAlpha(symbol.color, symbol.opacity) }),
      rotation: -rotation + Math.PI / 2,
      rotateWithView: true,
    }),
  });
}

/**
 * Point marker for a style spec. A registry icon renders as `ol/style/Icon`
 * from an inline SVG; circles use `ol/style/Circle`; every other shape is a
 * `RegularShape` built from the shared polygon descriptors so the Canvas engine
 * draws the same geometry MapLibre rasterizes.
 */
function pointImageFromSpec(
  spec: OlVectorStyleSpec,
  fillColor: string,
): CircleStyle | RegularShape | Icon {
  const fill = new Fill({ color: fillColor });
  const stroke = new Stroke({ color: '#ffffff', width: 1 });
  const radius = Math.max(1, spec.pointRadius);
  const rotationRad = ((spec.pointRotationDeg ?? 0) * Math.PI) / 180;

  const iconEntry = spec.pointSymbolId ? peekPointIcon(spec.pointSymbolId) : null;
  if (iconEntry) {
    const diameter = radius * 2;
    return new Icon({
      src: pointIconDataUrl(iconEntry, fillColor),
      width: diameter,
      height: diameter,
      rotation: rotationRad,
    });
  }

  const params = regularShapeParamsFor(spec.pointShape ?? 'circle');
  if (!params) {
    return new CircleStyle({ radius, fill, stroke });
  }
  return new RegularShape({
    points: params.points,
    radius,
    radius2: params.radius2Ratio == null ? undefined : radius * params.radius2Ratio,
    angle: params.angleRad,
    rotation: rotationRad,
    fill,
    stroke,
  });
}

/**
 * OpenLayers `tileLoadFunction` that pulls MVT bytes out of a PMTiles archive.
 *
 * OL tile coordinates are XYZ-style (`[z, x, y]`, y from the top), which is
 * exactly what PMTiles indexes by, so the coordinate passes through untouched.
 * A missing tile is an empty tile, not an error — sparse archives are normal.
 */
export function pmtilesVectorTileLoader(
  archive: PmtilesTileReader,
  format: MVT,
  onError?: (err: unknown) => void,
): (tile: unknown) => void {
  return (tile: unknown) => {
    const vectorTile = tile as OlVectorTile<RenderFeature>;
    vectorTile.setLoader((extent: Extent, _resolution: number, projection: Projection) => {
      const coord = vectorTile.getTileCoord();
      if (!coord) {
        vectorTile.setFeatures([]);
        return;
      }
      const [z, x, y] = coord;
      void archive
        .getZxy(z, x, y)
        .then((result) => {
          const data = result?.data;
          if (!data || data.byteLength === 0) {
            vectorTile.setFeatures([]);
            return;
          }
          const features = format.readFeatures(data, {
            extent,
            featureProjection: projection,
          }) as RenderFeature[];
          vectorTile.setFeatures(features);
        })
        .catch((err: unknown) => {
          onError?.(err);
          vectorTile.setFeatures([]);
        });
    });
  };
}

export interface CreateVectorLayerOptions {
  readonly zIndex: number;
  readonly opacity?: number;
}

export interface PmtilesVectorLayerResult {
  readonly olLayer: VectorTileLayer;
  /** Archive extent in WGS84, for "zoom to layer" and diagnostics. */
  readonly bounds: readonly [number, number, number, number] | null;
  readonly sourceLayers: readonly string[];
}

export async function createPmtilesVectorTileLayer(
  layer: Layer,
  url: string,
  options: CreateVectorLayerOptions,
): Promise<PmtilesVectorLayerResult> {
  const archive = new PMTiles(url);
  const info = await readPmtilesArchiveInfo(url).catch(() => null);
  const minZoom = info?.header?.minZoom ?? DEFAULT_MIN_ZOOM;
  const maxZoom = info?.header?.maxZoom ?? DEFAULT_MAX_ZOOM;
  const bounds = info?.header?.bounds ?? null;

  const format = new MVT();
  const source = new VectorTileSource({
    format,
    // Never fetched — `tileLoadFunction` reads the archive by tile coordinate.
    url: 'pmtiles://{z}/{x}/{y}',
    minZoom,
    maxZoom,
    tileLoadFunction: pmtilesVectorTileLoader(archive, format, (err) => {
      console.warn('[CanvasMapAdapter] PMTiles tile read failed', layer.id, err);
    }) as never,
  });

  let extent: Extent | undefined;
  if (bounds) {
    extent = transformExtent([...bounds], 'EPSG:4326', 'EPSG:3857');
  }

  const olLayer = new VectorTileLayer({
    source,
    extent,
    style: vectorStyleFunctionForLayer(layer),
    zIndex: options.zIndex,
    opacity: options.opacity ?? 1,
    declutter: false,
    // Soft-VM guard: no re-render mid-gesture (same policy as the raster tiles).
    updateWhileAnimating: false,
    updateWhileInteracting: false,
    properties: { geosoftId: layer.id },
  });

  return { olLayer, bounds, sourceLayers: info?.vectorLayerIds ?? [] };
}

/**
 * Accept a bare FeatureCollection, a single Feature, or an OGC API Features
 * response — the same shapes the MapLibre GeoJSON path tolerates.
 */
export function normalizeFeatureCollection(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  if (record.type === 'FeatureCollection' && Array.isArray(record.features)) return record;
  if (record.type === 'Feature') return { type: 'FeatureCollection', features: [record] };
  if (Array.isArray(record.features)) {
    return { type: 'FeatureCollection', features: record.features };
  }
  return null;
}

export async function createGeoJsonVectorLayer(
  layer: Layer,
  url: string,
  options: CreateVectorLayerOptions,
): Promise<VectorLayer | null> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const collection = normalizeFeatureCollection(await response.json());
  if (!collection) {
    console.warn('[CanvasMapAdapter] GeoJSON payload has no features', layer.id);
    return null;
  }

  const transformedCollection = await transformGeoJsonCoordinates(
    collection,
    layer.sourceCrsOverride,
    'EPSG:3857',
  );
  const source = new VectorSource({
    features: new GeoJSON().readFeatures(transformedCollection, {
      dataProjection: 'EPSG:3857',
      featureProjection: 'EPSG:3857',
    }),
  });

  return new VectorLayer({
    source,
    style: vectorStyleFunctionForLayer(layer),
    zIndex: options.zIndex,
    opacity: options.opacity ?? 1,
    updateWhileAnimating: false,
    updateWhileInteracting: false,
    properties: { geosoftId: layer.id },
  });
}
